/**
 * Agency Portal — Leads section (§4.4).
 *
 * Widget-sourced leads only (per founder decision 13.1). Each row is a
 * scan a visitor ran through the agency's embedded widget. The Scan
 * widget section keeps setup only; this is the inbox.
 *
 * v4 UX (2026-10-10) — the empty state branches on the WIDGET state,
 * not just a 403: an agency that provisioned a key but hasn't pasted
 * the snippet sees "No leads yet. Leads appear here when someone uses
 * the scan box on your site." instead of a misleading "Set up widget"
 * CTA.
 *
 * Agency gaps fix 2026-10-11 — per-row Delete + bulk delete with a
 * `ConfirmDialog`. The server routes already exist (`DELETE /v1/agency
 * /widget/leads/:lead_id` and `POST /v1/agency/widget/leads/bulk-
 * delete`); the dashboard just didn't surface them.
 */

import { Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import {
  bulkRemoveWidgetLeads,
  getAgencyWidget,
  getAgencyWidgetLeads,
  removeWidgetLead,
  type AgencyWidgetLeadWire,
  type AgencyWidgetWire,
} from '../../api/agency-widget.js';
import { useC } from '../../lib/theme.js';
import { Button, Card, ConfirmDialog, Tooltip } from '../../components/atoms/index.js';
import type { PortalSection } from './portal-sections.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; leads: readonly AgencyWidgetLeadWire[]; widget: AgencyWidgetWire | null }
  | { kind: 'forbidden'; reason: string }
  | { kind: 'error'; message: string };

export interface PortalLeadsProps {
  readonly goTo: (section: PortalSection) => void;
}

export function PortalLeads({ goTo }: PortalLeadsProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  /** Agency gaps fix 2026-10-11 — per-row + bulk delete.
   *
   *  `selected` is a Set of `lead_id`s. `confirmDelete` is either
   *  `{ kind: 'single', lead_id, domain }` or `{ kind: 'bulk',
   *  lead_ids }`. The ConfirmDialog shows while non-null.
   *
   *  `rowError` surfaces non-dialog failures (network, 403); the
   *  dialog's own message handles its branches. */
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<
    | null
    | { kind: 'single'; lead_id: string; domain: string }
    | { kind: 'bulk'; lead_ids: readonly string[] }
  >(null);
  const [deleting, setDeleting] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const loadLeads = useCallback(
    async (): Promise<void> => {
      if (http === null) return;
      // Fetch leads AND widget together so the empty state can branch
      // on real widget presence (not just a 403).
      const [leadsR, widgetR] = await Promise.all([
        getAgencyWidgetLeads(http, { limit: 100 }),
        getAgencyWidget(http),
      ]);
      if (leadsR.kind === 'forbidden') {
        setState({ kind: 'forbidden', reason: leadsR.reason });
        return;
      }
      if (leadsR.kind === 'error') {
        setState({ kind: 'error', message: leadsR.message });
        return;
      }
      const widget = widgetR.kind === 'ok' ? widgetR.widget : null;
      setState({ kind: 'ok', leads: leadsR.leads, widget });
    },
    [http],
  );

  useEffect(() => {
    void loadLeads();
  }, [loadLeads]);

  const toggleSelected = (lead_id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lead_id)) next.delete(lead_id);
      else next.add(lead_id);
      return next;
    });
  };

  const toggleSelectAll = (all: boolean): void => {
    if (state.kind !== 'ok') return;
    setSelected(all ? new Set(state.leads.map((l) => l.lead_id)) : new Set());
  };

  const confirmSingle = (lead: AgencyWidgetLeadWire): void => {
    setRowError(null);
    setConfirmDelete({
      kind: 'single',
      lead_id: lead.lead_id,
      domain: lead.domain,
    });
  };

  const confirmBulk = (): void => {
    setRowError(null);
    const lead_ids = Array.from(selected);
    if (lead_ids.length === 0) return;
    setConfirmDelete({ kind: 'bulk', lead_ids });
  };

  const doDelete = async (): Promise<void> => {
    if (http === null || confirmDelete === null || deleting) return;
    setDeleting(true);
    setRowError(null);
    if (confirmDelete.kind === 'single') {
      const target = confirmDelete.lead_id;
      const r = await removeWidgetLead(http, target);
      setDeleting(false);
      // Close the dialog either way so an error below the table is
      // visible rather than hidden behind the modal.
      setConfirmDelete(null);
      if (r.kind === 'ok' || r.kind === 'not_found') {
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(target);
          return next;
        });
        await loadLeads();
        return;
      }
      if (r.kind === 'forbidden') {
        setRowError(humanizeForbidden(r.reason));
      } else {
        setRowError(r.message);
      }
      return;
    }
    const r = await bulkRemoveWidgetLeads(http, confirmDelete.lead_ids);
    setDeleting(false);
    setConfirmDelete(null);
    if (r.kind === 'ok') {
      setSelected(new Set());
      await loadLeads();
      return;
    }
    if (r.kind === 'empty') {
      setRowError('Select at least one lead to delete.');
      return;
    }
    if (r.kind === 'too_many_rows') {
      setRowError(`Delete at most ${r.max} leads at a time.`);
      return;
    }
    if (r.kind === 'forbidden') {
      setRowError(humanizeForbidden(r.reason));
      return;
    }
    setRowError(r.message);
  };

  if (state.kind === 'loading') {
    return (
      <Card className="p-5">
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>Loading…</div>
      </Card>
    );
  }

  if (state.kind === 'error') {
    return (
      <Card className="p-5">
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>{state.message}</div>
      </Card>
    );
  }

  if (state.kind === 'forbidden' || (state.kind === 'ok' && state.leads.length === 0)) {
    // v4 (2026-10-10) — distinguish three empty paths:
    //   1. forbidden → feature-off; "set up widget" CTA
    //   2. widget_null → never provisioned; "set up widget" CTA
    //   3. widget_present → provisioned but no leads yet; "paste the
    //      snippet on your site" copy with no "set up widget" CTA
    //      (it's already set up — the Scan widget page tells them the
    //      next step).
    const widget = state.kind === 'ok' ? state.widget : null;
    const widgetSetUp = widget !== null;
    const widgetReady = widgetSetUp && (widget!.origins ?? []).length > 0;
    const body = !widgetSetUp
      ? 'Set up the scan widget to start collecting leads.'
      : widgetReady
        ? 'No leads yet. Leads appear here when someone uses the scan box on your site.'
        : 'Add a website to your widget, then paste the snippet so visitors can scan their site.';
    return (
      <Card className="p-6" data-testid="portal-leads-empty">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            letterSpacing: 2,
            color: C.muted,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          LEADS
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            color: C.text,
            marginBottom: 4,
          }}
        >
          No leads yet.
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            marginBottom: 14,
          }}
        >
          {body}
        </div>
        <Button
          variant="primary"
          data-testid="portal-leads-empty-cta"
          onClick={() => goTo('widget')}
        >
          {widgetReady ? 'Open installation instructions' : 'Set up widget'}
        </Button>
      </Card>
    );
  }

  if (state.kind !== 'ok') return <></>;

  const all_selected =
    state.leads.length > 0 && state.leads.every((l) => selected.has(l.lead_id));

  return (
    <>
      {selected.size > 0 ? (
        <div
          data-testid="portal-leads-bulk-bar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            padding: '10px 12px',
            marginBottom: 10,
            background: C.surface2,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
          }}
        >
          <span>
            {selected.size} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </Button>
            <Button
              variant="danger"
              size="sm"
              data-testid="portal-leads-bulk-delete"
              icon={Trash2}
              onClick={confirmBulk}
            >
              Delete {selected.size}
            </Button>
          </div>
        </div>
      ) : null}

      <Card className="p-5" data-testid="portal-leads-table-card">
        <div style={{ overflowX: 'auto', margin: '0 -2px' }}>
        <table
          data-testid="portal-leads-table"
          style={{
            width: '100%',
            minWidth: 580,
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr>
              <Th C={C}>
                <input
                  type="checkbox"
                  aria-label="Select all leads on this page"
                  data-testid="portal-leads-select-all"
                  checked={all_selected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
              </Th>
              <Th C={C}>Domain</Th>
              <Th C={C}>Source</Th>
              <Th C={C}>Agent-ready checks</Th>
              <Th C={C}>Scanned</Th>
              <Th C={C}>Email</Th>
              <Th C={C}>Report</Th>
              <Th C={C}>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {state.leads.map((lead) => (
              <tr
                key={lead.lead_id}
                style={{ borderTop: `1px solid ${C.borderSoft}` }}
              >
                <td style={{ padding: '10px 6px' }}>
                  <input
                    type="checkbox"
                    aria-label={`Select ${lead.domain}`}
                    data-testid={`portal-leads-select-${lead.lead_id}`}
                    checked={selected.has(lead.lead_id)}
                    onChange={() => toggleSelected(lead.lead_id)}
                  />
                </td>
                <td style={{ padding: '10px 6px', fontFamily: monoFont, fontSize: 12 }}>
                  {lead.domain}
                </td>
                <td style={{ padding: '10px 6px' }}>
                  {lead.client_signal === 'browser' ? (
                    <span style={{ color: C.muted }}>Your site</span>
                  ) : (
                    <Tooltip content="This scan came from a page or tool we could not confirm is your site.">
                      <span style={{ color: C.amberFg }}>Unverified</span>
                    </Tooltip>
                  )}
                </td>
                <td style={{ padding: '10px 6px', fontFamily: monoFont, fontSize: 12 }}>
                  {lead.signals_present === null || lead.signals_total === null
                    ? '—'
                    : `${lead.signals_present} of ${lead.signals_total} pass`}
                </td>
                <td style={{ padding: '10px 6px', color: C.muted }}>
                  {formatWhen(lead.created_at)}
                </td>
                <td style={{ padding: '10px 6px', fontFamily: monoFont, fontSize: 12 }}>
                  {lead.email !== null && lead.email !== undefined
                    ? lead.email
                    : <span style={{ color: C.muted }}>—</span>}
                </td>
                <td style={{ padding: '10px 6px' }}>
                  {lead.report_url === null ? (
                    <span style={{ color: C.muted }}>—</span>
                  ) : (
                    <a
                      href={lead.report_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: C.text, textDecoration: 'underline' }}
                    >
                      Open
                    </a>
                  )}
                </td>
                <td style={{ padding: '10px 6px' }}>
                  <button
                    type="button"
                    onClick={() => confirmSingle(lead)}
                    data-testid={`portal-leads-delete-${lead.lead_id}`}
                    aria-label={`Delete lead ${lead.domain}`}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: C.coralFg,
                      cursor: 'pointer',
                      padding: 4,
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {rowError !== null ? (
          <div
            data-testid="portal-leads-row-error"
            role="alert"
            style={{
              marginTop: 10,
              fontFamily: sansFont,
              fontSize: 13,
              color: C.coralFg,
            }}
          >
            {rowError}
          </div>
        ) : null}
      </Card>

      <ConfirmDialog
        open={confirmDelete !== null}
        title={
          confirmDelete === null
            ? 'Delete lead?'
            : confirmDelete.kind === 'single'
              ? `Delete lead for ${confirmDelete.domain}?`
              : `Delete ${confirmDelete.lead_ids.length} leads?`
        }
        body={
          confirmDelete === null
            ? null
            : confirmDelete.kind === 'single' ? (
              <>
                This removes the lead from your Leads list. It can't be
                undone.
              </>
            ) : (
              <>
                This removes the selected leads from your Leads list. It
                can't be undone.
              </>
            )
        }
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        confirmTone="coral"
        onConfirm={() => void doDelete()}
        onClose={() => {
          if (deleting) return;
          setConfirmDelete(null);
        }}
      />
    </>
  );
}

function humanizeForbidden(reason: string): string {
  switch (reason) {
    case 'agency_feature_disabled':
      return 'The agency feature is not enabled on this account.';
    case 'role_insufficient':
      return 'Only owners and admins can delete leads.';
    case 'acting_session_denied':
      return 'Return to your home account first.';
    default:
      return 'You do not have permission to delete this lead.';
  }
}

function Th({ children, C }: { children: React.ReactNode; C: ReturnType<typeof useC> }): JSX.Element {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 6px',
        color: C.muted,
        fontWeight: 400,
        fontFamily: monoFont,
        fontSize: 10,
        letterSpacing: 1,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </th>
  );
}

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  } catch {
    return '—';
  }
}
